import React, { useRef, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { getBloomreachModel } from '@yme/selectors/common';
import { getIsInAuthor, getRootLang } from '@yme/selectors/index';
import { PixelEvent, setPixelEvent } from '@yme/utils/bloomreach';
import { SearchCategoryItem } from './SearchCategoryItem';
import { SearchContentItem } from './SearchContentItem';
import { SearchProductItem } from './SearchProductItem';
import { SearchSuggestionItem } from './SearchSuggestionItem';
import { SearchListStyled, SeparatorStyled } from './styled';
import { useSearchKeyboardControl } from './useSearchKeyboardControl';
export function SearchContent({ querySuggestions = [], productSearchSuggestions = [], contentSearchSuggestions = [], typeFacets = [], searchedValue, searchResultsPageUrl, activeElement, isRightToLeft, isSearchPage = false, onClickLink, }) {
    const listRef = useRef(null);
    const language = useSelector(getRootLang);
    const { productCatalog } = useSelector(getBloomreachModel);
    const isInAuthor = useSelector(getIsInAuthor);
    useSearchKeyboardControl({
        activeElement,
        listRef: listRef.current,
    });
    const suggestionsLength = querySuggestions.length;
    const productLength = productSearchSuggestions.length;
    const contentLength = contentSearchSuggestions.length;
    const handleClickSearchEventLink = useCallback((query) => {
        if (!isInAuthor) {
            setPixelEvent(PixelEvent.SEARCH, {
                q: query,
                domain_key: productCatalog,
            });
        }
        onClickLink();
    }, [isInAuthor, onClickLink, productCatalog]);
    const handleClickSuggestEventLink = useCallback((query) => {
        if (!isInAuthor) {
            setPixelEvent(PixelEvent.SUGGEST, {
                aq: searchedValue,
                q: query,
                domain_key: productCatalog,
            });
        }
        onClickLink();
    }, [isInAuthor, onClickLink, searchedValue, productCatalog]);
    const Suggestions = useMemo(() => querySuggestions.map((item, index) => {
        const isActive = activeElement === index;
        return (React.createElement(SearchSuggestionItem, { key: item.query, suggestion: item, textToHighlight: searchedValue, searchResultsPageUrl: searchResultsPageUrl, isActive: isActive, handleClickLink: handleClickSuggestEventLink }));
    }), [
        activeElement,
        handleClickSuggestEventLink,
        searchedValue,
        querySuggestions,
        searchResultsPageUrl,
    ]);
    const Products = useMemo(() => productSearchSuggestions.map((item, index) => {
        const isActive = activeElement === index + suggestionsLength;
        const hasSeparator = index === 0 && suggestionsLength > 0;
        return (React.createElement(React.Fragment, null,
            hasSeparator && React.createElement(SeparatorStyled, null),
            React.createElement(SearchProductItem, { key: item.pid, product: item, language: language, isRightToLeft: isRightToLeft, isActive: isActive, handleClickLink: handleClickSuggestEventLink })));
    }), [
        activeElement,
        handleClickSuggestEventLink,
        isRightToLeft,
        language,
        productSearchSuggestions,
        suggestionsLength,
    ]);
    const Content = useMemo(() => contentSearchSuggestions.map((item, index) => {
        const isActive = activeElement === index + suggestionsLength + productLength;
        const hasSeparator = index === 0 && suggestionsLength + productLength > 0;
        return (React.createElement(React.Fragment, null,
            hasSeparator && React.createElement(SeparatorStyled, null),
            React.createElement(SearchContentItem, { key: item.item_id, page: item, handleClickLink: handleClickSuggestEventLink, isActive: isActive })));
    }), [
        activeElement,
        contentSearchSuggestions,
        handleClickSuggestEventLink,
        productLength,
        suggestionsLength,
    ]);
    const Categorization = useMemo(() => typeFacets.map((item, index) => {
        const isActive = activeElement === index + suggestionsLength + productLength + contentLength;
        const hasSeparator = index === 0 && suggestionsLength + productLength + contentLength > 0;
        return (React.createElement(React.Fragment, null,
            hasSeparator && React.createElement(SeparatorStyled, null),
            React.createElement(SearchCategoryItem, { key: item.name, item: item, isActive: isActive, searchedValue: searchedValue, searchResultsPageUrl: searchResultsPageUrl, handleClickLink: handleClickSearchEventLink })));
    }), [
        activeElement,
        contentLength,
        handleClickSearchEventLink,
        searchedValue,
        productLength,
        searchResultsPageUrl,
        suggestionsLength,
        typeFacets,
    ]);
    return (React.createElement(SearchListStyled, { ref: listRef, isRightToLeft: isRightToLeft, isSearchPage: isSearchPage },
        Suggestions,
        Products,
        Content,
        Categorization));
}
